import assert from 'assert';

import typeCheck from 'common/util/typeCheck';
import round from 'common/util/round';

import {
  LISTING_DATA_SOURCE_SUNROOM,
} from 'common/listingConstants';

import {
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
  HOUSEHOLD_APPLICATION_STATUS_LIST,
  HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP,
  HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING,
} from 'common/householdConstants';

import {
  SR_PET_SCREENING_STATUS_COMPLETED,
  SR_PET_SCREENING_STATUS_IN_PROGRESS,
  SR_PET_SCREENING_STATUS_NOT_REQUIRED,
} from 'common/petScreeningConstants';

import {
  PAYMENT_ACCOUNT_CARD,
  PAYMENT_ACCOUNT_BANK_ACCOUNT,
} from 'common/paymentAccountConstants';

import {
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  getCardStripeFee,
  getBankStripeFee,
} from 'common/stripeHelpers';

import {
  partnerListingFeeSettingsFieldgroup,
  pickPartnerListingFeeSettingsFieldgroup,
} from 'server/models/Listing/fieldgroups';

import {
  householdListingFeesFieldgroup,
} from 'server/models/HouseholdListing/fieldgroups';
import { CHARGE_TYPE_OFFLINE } from './stripeConstants';

export function makeSecurityDepositPaymentLink({ appUrl, householdId, listingId }) {
  return (
    `${appUrl}/security-deposit/${householdId}/${listingId}`
  );
}

export function getHouseholdsByStatus({ households, householdStatus }) {
  return households.filter(({ status }) => status === householdStatus);
}

export function getHouseholdApplicationsLastStatusUpdateDate(householdListing) {
  const lastStatusUpdateDates = householdListing.applications
    .map(({ lastStatusUpdateDate }) => lastStatusUpdateDate)
    .filter((lastStatusUpdateDate) => lastStatusUpdateDate)
    .map((lastStatusUpdateDate) => new Date(lastStatusUpdateDate));

  return lastStatusUpdateDates.length === 0
    ? null
    : Math.max(...lastStatusUpdateDates);
}

/**
 * Prepares household fields for display in the UI component.
 *
 * @param  {Object} household Household item.
 * @return {Object}           Object with household display fields.
 */
export function extractHouseholdDisplayFields({ renterMoveInDate, leaseStartDate, leadRenter }) {
  typeCheck('renterMoveInDate::DateString', renterMoveInDate);
  typeCheck('leaseStartDate::Maybe DateString', leaseStartDate);
  typeCheck('leadRenter::NonEmptyObject', leadRenter);
  return {
    moveInDateDisplay: (
      momentWithAustinTimezone(leaseStartDate || renterMoveInDate).format('MM/DD/YYYY') +
      (leaseStartDate ? ' Lease Start Date' : ' Move-in Date')
    ),
    signUpDateDisplay: `${momentWithAustinTimezone(leadRenter.createdDate).format('MM/DD/YYYY')} Signed Up`,
    timeSinceLastFrontMessageDisplay: (leadRenter.frontLastMessageReceivedDate
      ? `${momentWithAustinTimezone(leadRenter.frontLastMessageReceivedDate).fromNow(true)} since the last Front message` // eslint-disable-line max-len
      : ''
    ),
  };
}

/**
 * Given an object of type householdListing, it returns the status of the householdListing
 * based off the weakest applicant status.
 *
 * @param  {Object} householdListing householdListing item.
 * @return {string}                  The weakest link status of the householdListing applicants.
 */
export function getHouseholdListingWeakestLinkStatus({ householdListing }) {
  typeCheck('leadRenter::NonEmptyObject', householdListing);
  const applicationIndexes = [];
  householdListing.applications.forEach((application) => {
    applicationIndexes.push(HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(application.status));
  });

  return HOUSEHOLD_APPLICATION_STATUS_LIST[Math.min(...applicationIndexes)];
}

export function getStrongestApplicationStatus({ householdApplications }) {
  typeCheck('householdApplications::Array', householdApplications);

  const applicationIndexes = [];
  householdApplications.forEach((application) => {
    applicationIndexes.push(HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(application.status));
  });

  return HOUSEHOLD_APPLICATION_STATUS_LIST[Math.max(...applicationIndexes)];
}

/**
 * Given an array of householdListings it will return an array with the householdListings
 * ordered by 'strongest link'. Strongest link being defined by the householdListing that the
 * applicant(s) have made the most progress on.
 *
 * @param  {array} householdListings An array of Household Listings.
 * @return {array}                   Ordered array of Household Listings
 */
export function orderHouseholdListingsByStrongestLink({ householdListings }) {

  const orderedListings = householdListings.sort((listingA, listingB) => {

    const aApplicationIndexes = [];
    listingA.applications.forEach((application) => {
      aApplicationIndexes.push(HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(application.status));
    });

    const bApplicationIndexes = [];
    listingB.applications.forEach((application) => {
      bApplicationIndexes.push(HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(application.status));
    });

    const listingAMinIndex = Math.min(...aApplicationIndexes);
    const listingdBMinIndex = Math.min(...bApplicationIndexes);

    return listingdBMinIndex - listingAMinIndex;
  });

  return orderedListings;
}

export function getHouseholdListingsByStatus({ household }) {
  const householdListingsActive = household.listingApplications.filter(({ status, householdListingStatus }) => ( // eslint-disable-line max-len
    status === HOUSEHOLD_LISTING_STATUS_ACTIVE
    || householdListingStatus === HOUSEHOLD_LISTING_STATUS_ACTIVE
  ));

  const householdListingsArchived = household.listingApplications.filter(({ status, householdListingStatus }) => ( // eslint-disable-line max-len
    status === HOUSEHOLD_LISTING_STATUS_ARCHIVED
    || householdListingStatus === HOUSEHOLD_LISTING_STATUS_ARCHIVED
  ));

  if (householdListingsActive.length === 0) {
    householdListingsArchived.sort((householdListingA, householdListingB) => {
      const lastStatusUpdateDateA = getHouseholdApplicationsLastStatusUpdateDate(householdListingA) || 0;
      const lastStatusUpdateDateB = getHouseholdApplicationsLastStatusUpdateDate(householdListingB) || 0;
      return lastStatusUpdateDateA - lastStatusUpdateDateB;
    });
  }

  return {
    householdListingsActive,
    householdListingsArchived,
    allHouseholdListingsAreArchived: householdListingsActive.length === 0,
  };
}

export function isAllRoommatesApplicationsAtSameStatus({ status, applications }) {
  const statusParentGroup = HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING[status];
  for (const application of applications) {
    const applicationStatusParentGroup = HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING[application.status]; // eslint-disable-line max-len
    if (applicationStatusParentGroup !== statusParentGroup) {
      return false;
    }
  }
  return true;
}

export function getCurrentUserRenterProfile(params) {
  const { state } = params;

  if (!params.currentUser && !params.renterProfilesByUserId) {
    typeCheck('state::NonEmptyObject', state);
  }

  const currentUser = params.currentUser || state.get(['currentUser']);
  const renterProfilesByUserId = (
    params.renterProfilesByUserId || state.get(['household', 'renterProfilesByUserId'])
  );

  typeCheck('currentUser::NonEmptyObject', currentUser);
  return renterProfilesByUserId && renterProfilesByUserId[currentUser.id] || null;
}

export function groupKanbanBoardHouseholdsByApplicationsStatus(kanbanBoardHouseholds) {
  const householdsByApplicationsStatus = HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP
    .reduce((memo, applicationStatus) => ({ ...memo, [applicationStatus]: [] }), {});

  kanbanBoardHouseholds.forEach((household) => {
    const {
      householdListingsActive,
      householdListingsArchived,
      allHouseholdListingsAreArchived,
    } = getHouseholdListingsByStatus({ household: household });

    const householdListings = allHouseholdListingsAreArchived
      ? householdListingsArchived
      : householdListingsActive;

    const applicationStatusIndexes = householdListings.reduce((memo, { applications }) => {
      const applicationIndexes = [];
      applications.forEach((application) => {
        applicationIndexes.push(HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(application.status));
      });

      memo.push(Math.max(...applicationIndexes));

      return memo;
    }, []);

    const applicationStatus = HOUSEHOLD_APPLICATION_STATUS_LIST[Math.max(...applicationStatusIndexes)];
    const applicationStatusParentGroup = HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING[applicationStatus]; // eslint-disable-line max-len

    if (applicationStatus) {
      householdsByApplicationsStatus[applicationStatusParentGroup].push(household);
    }
  });

  return householdsByApplicationsStatus;
}

export function sortHouseholdsByStrongestApplicationStatus({ households }) {
  const strongestHouseholdApplicationStatusByHouseholdId = households.reduce((memo, household) => ({
    ...memo,
    [household.id]: getStrongestApplicationStatus({
      householdApplications: household.householdApplications,
    }),
  }), {});

  const householdsOrderedByStrongestApplicationStatus = households.sort((householdA, householdB) => {
    const applicationStatusIndexA = HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(
      strongestHouseholdApplicationStatusByHouseholdId[householdA.id]
    );
    const applicationStatusIndexB = HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(
      strongestHouseholdApplicationStatusByHouseholdId[householdB.id]
    );
    return applicationStatusIndexB - applicationStatusIndexA;
  });

  return householdsOrderedByStrongestApplicationStatus;
}

export function getDownloadHouseholdDocumentsUrl({
  appUrl,
  listingId,
  householdId,
  regenerateDocs,
}) {
  const stringQuery = JSON.stringify({
    listingId,
    householdId,
    regenerateDocs,
  });
  const encodedQuery = encodeURIComponent(stringQuery);

  return `${appUrl}/bundle-files/households-application-documents?data=${encodedQuery}`; // eslint-disable-line max-len
}

export function getHouseholdPets({ householdApplications }) {
  const pets = householdApplications
    .reduce((memo, application) => {
      const applicationPets = (application.pets || []).filter((pet) => pet.name && pet.type);
      return [
        ...memo,
        ...applicationPets,
      ];
    }, [])
    .reduce((memo, pet) => {
      if (!memo.find((_pet) => _pet.name.toLowerCase() === pet.name.toLowerCase())) {
        memo.push(pet);
      }
      return memo;
    }, []);

  const petsByType = pets.reduce((memo, pet) => {
    let petType = pet.type;
    if (pet.isEsa) {
      petType = `ESA ${petType}`;
    }

    const petGroup = memo[petType] || [];
    return {
      ...memo,
      [petType]: [
        ...petGroup,
        pet,
      ],
    };
  }, {});

  const nonEsaPets = pets.filter(({ isEsa }) => !isEsa);

  return { pets, nonEsaPets, petsByType };
}

export function getPetScreeningStatus({ pets, partner }) {
  const petsToVerify = pets.filter((pet) => pet.type === 'dog' || pet.type === 'cat');

  if (petsToVerify.length > 0 && partner?.petScreeningUrl) {
    const verificationCompleted = petsToVerify.every((pet) => !pet.petScreeningProfileId);
    if (verificationCompleted) {
      return SR_PET_SCREENING_STATUS_COMPLETED;
    }
    return SR_PET_SCREENING_STATUS_IN_PROGRESS;
  }

  return SR_PET_SCREENING_STATUS_NOT_REQUIRED;
}

export function getHouseholdVehicles(householdApplications) {
  const householdVehicles = householdApplications
    .reduce((memo, application) => {
      const applicationVehicles = (application.vehicles || []);
      return [
        ...memo,
        ...applicationVehicles,
      ];
    }, [])
    .reduce((memo, vehicle) => {
      if (!memo.find((_vehicle) => {
        return _vehicle.licensePlateNumber.toLowerCase() === vehicle.licensePlateNumber.toLowerCase();
      })) {
        memo.push(vehicle);
      }
      return memo;
    }, []);

  return householdVehicles;
}

/**
 * Calculates a household's pet deposit and fees, and the security deposit amount.
 *
 * @param  {number}  params.pets     The number of pets to use for calculation.
 * @param  {Object}  params.market   The market object to use for the security deposit label.
 * @param  {Object}  params.listing  The listing object to use for calculations.
 * @return {Object}  Object with various calculations, and the total deposit fees amount.
 */
export function calculateHouseholdDepositFees({
  pets,
  market,
  listing,
  householdListing,
  type,
}) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('market::Maybe NonEmptyObject', market);
  typeCheck('listingDataSource::NonEmptyString', listing.dataSource);
  typeCheck('pets::Array', pets);
  typeCheck('type::Maybe NonEmptyString', type);
  assert(
    partnerListingFeeSettingsFieldgroup.every((fieldName) => fieldName in listing),
    `Listing is missing fee settings fields. Should have: ${partnerListingFeeSettingsFieldgroup}`,
  );
  assert(
    householdListingFeesFieldgroup.every((fieldName) => fieldName in householdListing),
    `HouseholdListing is missing fee fields. Should have: ${householdListingFeesFieldgroup}`,
  );
  typeCheck('securityDepositIncreaseMultiplier::PositiveNumber', householdListing.securityDepositIncreaseMultiplier); // eslint-disable-line max-len

  const listingFeeSettings = pickPartnerListingFeeSettingsFieldgroup(listing);
  const securityDepositIncreaseMultiplier = householdListing.securityDepositIncreaseMultiplier;
  const securityDepositLabel = getMarketSecurityDepositLabel(market, true);

  const feesForCharge = {
    listingFeeSettings: listingFeeSettings,
    pets: pets,
    firstMonthRent: 0,
    monthlyPetRent: 0,
    additionalMonthlyPetRent: 0,
    securityDeposit: 0,
    petDeposit: 0,
    additionalPetDeposit: 0,
    petFees: 0,
    additionalPetFee: 0,
    adminFee: 0,
    stripeFee: 0,
    totalWithoutStripeFees: null,
    total: null,
    items: [],
  };

  if (listing.dataSource === LISTING_DATA_SOURCE_SUNROOM) {
    if (listingFeeSettings.collectFirstMonth) {
      // First Month’s Rent
      feesForCharge.firstMonthRent = householdListing.rentPrice || listingFeeSettings.price;
      feesForCharge.items.push({
        title: 'First Month\'s Rent',
        subtitle: `Collected with ${securityDepositLabel}`,
        value: feesForCharge.firstMonthRent,
        category: 'rent',
      });

      if (listingFeeSettings.monthlyPetRent && listing.collectPetRent) {
        if (pets.length >= 1) {
          feesForCharge.monthlyPetRent = listingFeeSettings.monthlyPetRent;
          feesForCharge.items.push({
            title: 'Pet Rent',
            value: feesForCharge.monthlyPetRent,
            quantity: 1,
            category: 'rent',
          });
        }
        if (pets.length >= 2 && listingFeeSettings.monthlyPetRentPerPet) {
          feesForCharge.additionalMonthlyPetRent = (pets.length - 1) * (listing.additionalMonthlyPetRent || 0); // eslint-disable-line max-len
          feesForCharge.items.push({
            title: 'Additional Monthly Pet Rent',
            value: feesForCharge.additionalMonthlyPetRent,
            quantity: pets.length - 1,
            category: 'rent',
          });
          feesForCharge.monthlyPetRent += feesForCharge.additionalMonthlyPetRent;
        }
      }

      // Pet Fee
      if (listingFeeSettings.collectPetFee) {
        if (pets.length >= 1) {
          feesForCharge.petFees = listingFeeSettings.petFee || 0;
          feesForCharge.items.push({
            title: 'Pet Fee',
            value: feesForCharge.petFees,
            quantity: 1,
            category: 'pet',
          });
        }
        // Additional Pet Fee
        if (pets.length >= 2 && listingFeeSettings.petFeePerPet) {
          feesForCharge.additionalPetFee = (pets.length - 1) * (listing.additionalPetFee || 0);
          feesForCharge.items.push({
            title: 'Additional Pet Fee',
            value: feesForCharge.additionalPetFee,
            quantity: pets.length - 1,
            category: 'pet',
          });
          feesForCharge.petFees += feesForCharge.additionalPetFee;
        }
      }
    }

    // Security Deposit
    feesForCharge.securityDeposit = listingFeeSettings.securityDeposit * securityDepositIncreaseMultiplier;
    feesForCharge.items.push({
      title: securityDepositLabel,
      value: feesForCharge.securityDeposit,
      category: 'deposit',
    });
    // Pet Deposit
    if (listingFeeSettings.collectPetDeposit) {
      if (pets.length >= 1) {
        feesForCharge.petDeposit = listingFeeSettings.petDeposit || 0;
        feesForCharge.items.push({
          title: 'Pet Deposit (Refundable)',
          value: feesForCharge.petDeposit,
          quantity: 1,
          category: 'pet',
        });
      }
      // Additional Pet Deposit
      if (pets.length >= 2 && listingFeeSettings.petDepositPerPet) {
        feesForCharge.additionalPetDeposit = (pets.length - 1) * (listing.additionalPetDeposit || 0);
        feesForCharge.items.push({
          title: 'Additional Pet Deposit (Refundable)',
          value: feesForCharge.additionalPetDeposit,
          quantity: pets.length - 1,
          category: 'pet',
        });
        feesForCharge.petDeposit += feesForCharge.additionalPetDeposit;
      }
    }
  }

  // Admin Fee
  if (listingFeeSettings.collectAdminFee && listing.administrationFee) {
    feesForCharge.adminFee = listing.administrationFee;
    feesForCharge.items.push({
      title: 'Administration Fee',
      value: feesForCharge.adminFee,
      quantity: 1,
      category: 'rent',
    });
  }

  feesForCharge.totalWithoutStripeFees = (
    feesForCharge.firstMonthRent
    + feesForCharge.monthlyPetRent
    + feesForCharge.securityDeposit
    + feesForCharge.petDeposit
    + feesForCharge.petFees
    + feesForCharge.adminFee
  );

  feesForCharge.cardStripeFee = getCardStripeFee(feesForCharge.totalWithoutStripeFees).stripeFee;
  feesForCharge.bankStripeFee = getBankStripeFee(feesForCharge.totalWithoutStripeFees).stripeFee;
  feesForCharge.cardTotal = round(feesForCharge.totalWithoutStripeFees + feesForCharge.cardStripeFee);
  feesForCharge.bankTotal = round(feesForCharge.totalWithoutStripeFees + feesForCharge.bankStripeFee);

  if (type === CHARGE_TYPE_OFFLINE) {
    feesForCharge.total = feesForCharge.totalWithoutStripeFees;
  }

  return feesForCharge;
}

export function calculateHouseholdDepositFeesWithPaymentAccount({
  depositFees,
  paymentAccount,
}) {
  typeCheck('depositFees::NonEmptyObject', depositFees);
  typeCheck('paymentAccount::NonEmptyObject', paymentAccount);

  let stripeFee = 0;

  switch (paymentAccount.type) {
    case PAYMENT_ACCOUNT_CARD: {
      stripeFee = depositFees.cardStripeFee;
      break;
    }
    case PAYMENT_ACCOUNT_BANK_ACCOUNT: {
      stripeFee = depositFees.bankStripeFee;
      break;
    }
  }

  return {
    stripeFee,
    total: round(depositFees.totalWithoutStripeFees + stripeFee),
  };
}

export function getLeaseParkingSpaces({ listing, applications }) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('applications::Array', applications);

  const renterVehicleCount = getHouseholdVehicles(applications).length;
  const maxListingVehicleCount = (listing.parkingSpaces || 0) + (listing.garageSpaces || 0);
  const parkingSpaces = Math.min(renterVehicleCount, maxListingVehicleCount);

  return parkingSpaces;
}
